/**
 * Servicio de notificaciones para el teléfono SIP
 * Gestiona las notificaciones del sistema para llamadas entrantes
 */
class NotificationService {
  constructor() {
    this.notificationPermission = null;
    this.activeNotification = null;
    this.notificationTimeout = null;
  }

  /**
   * Inicializa el servicio de notificaciones y solicita permiso si es necesario
   * @returns {Promise<boolean>} - True si se concedió o ya se tenía permiso
   */
  async initialize() {
    // Verificar si las notificaciones están soportadas
    if (!('Notification' in window)) {
      console.warn('Este navegador no soporta notificaciones de escritorio');
      return false;
    }

    // Verificar permiso actual
    if (Notification.permission === 'granted') {
      this.notificationPermission = 'granted';
      return true;
    } else if (Notification.permission !== 'denied') {
      // Solicitar permiso
      try {
        const permission = await Notification.requestPermission();
        this.notificationPermission = permission;
        return permission === 'granted';
      } catch (error) {
        console.error('Error al solicitar permiso de notificaciones:', error);
        return false;
      }
    }

    return Notification.permission === 'granted';
  }

  /**
   * Muestra una notificación de llamada entrante
   * @param {string} caller - Número o identificador de quien llama
   * @param {Function} onAccept - Callback para cuando el usuario acepta la llamada
   * @param {Function} onReject - Callback para cuando el usuario rechaza la llamada
   */
  showIncomingCallNotification(caller, onAccept, onReject) {
    // Si no hay permiso o ya hay una notificación activa, no hacer nada
    if (this.notificationPermission !== 'granted' || this.activeNotification) {
      return;
    }

    // Limpiar timeout previo si existe
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout);
      this.notificationTimeout = null;
    }

    // Crear notificación básica (sin acciones)
    const notification = new Notification('📞 Llamada entrante', {
      body: `Llamada de ${caller}\n\nHaz clic para contestar la llamada`,
      icon: this.getPhoneIcon(),
      tag: 'sip-incoming-call',
      requireInteraction: true, // La notificación no se cerrará automáticamente
      silent: false // Permitir sonido de notificación del sistema
    });

    // Manejar clic en la notificación - contestar llamada
    notification.onclick = (event) => {
      event.preventDefault();
      this.focusWindow();
      notification.close();
      this.activeNotification = null;
      
      // Ejecutar callback de aceptar
      if (onAccept && typeof onAccept === 'function') {
        onAccept();
      }
    };

    // Manejar error en la notificación
    notification.onerror = (error) => {
      console.error('Error en notificación:', error);
      this.activeNotification = null;
    };

    // Manejar cierre de la notificación
    notification.onclose = () => {
      this.activeNotification = null;
      if (this.notificationTimeout) {
        clearTimeout(this.notificationTimeout);
        this.notificationTimeout = null;
      }
    };

    // Guardar referencia a la notificación activa
    this.activeNotification = notification;

    // Configurar timeout para cerrar la notificación automáticamente después de 30 segundos
    // (las llamadas típicamente tienen un timeout de 30-60 segundos)
    this.notificationTimeout = setTimeout(() => {
      if (this.activeNotification) {
        this.activeNotification.close();
        this.activeNotification = null;
      }
    }, 30000);

    return notification;
  }

  /**
   * Cierra la notificación activa si existe
   */
  closeActiveNotification() {
    if (this.activeNotification) {
      try {
        this.activeNotification.close();
      } catch (error) {
        console.error('Error al cerrar notificación:', error);
      }
      this.activeNotification = null;
    }

    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout);
      this.notificationTimeout = null;
    }
  }

  /**
   * Muestra una notificación de llamada perdida
   * @param {string} caller - Número o identificador de quien llamó
   * @param {Function} onCallback - Función a ejecutar si el usuario hace clic en la notificación
   */
  showMissedCallNotification(caller, onCallback) {
    // Si no hay permiso, no hacer nada
    if (this.notificationPermission !== 'granted') {
      return;
    }

    // Crear notificación
    const notification = new Notification('📵 Llamada perdida', {
      body: `Llamada perdida de ${caller}\n\nHaz clic para llamar de vuelta`,
      icon: this.getPhoneIcon(),
      tag: 'sip-missed-call',
      silent: false
    });

    // Manejar clic en la notificación
    notification.onclick = (event) => {
      event.preventDefault();
      this.focusWindow();
      notification.close();
      
      // Ejecutar callback
      if (onCallback && typeof onCallback === 'function') {
        onCallback();
      }
    };

    // Manejar error
    notification.onerror = (error) => {
      console.error('Error en notificación de llamada perdida:', error);
    };

    // Cerrar automáticamente después de 10 segundos
    setTimeout(() => {
      try {
        notification.close();
      } catch (error) {
        // Ignorar error si ya se cerró
      }
    }, 10000);

    return notification;
  }

  /**
   * Muestra una notificación de estado del sistema
   * @param {string} title - Título de la notificación
   * @param {string} message - Mensaje de la notificación
   * @param {number} duration - Duración en milisegundos (por defecto 5 segundos)
   */
  showStatusNotification(title, message, duration = 5000) {
    // Si no hay permiso, no hacer nada
    if (this.notificationPermission !== 'granted') {
      return;
    }

    const notification = new Notification(title, {
      body: message,
      icon: this.getPhoneIcon(),
      tag: 'sip-status',
      silent: true // Sin sonido para notificaciones de estado
    });

    // Cerrar automáticamente
    setTimeout(() => {
      try {
        notification.close();
      } catch (error) {
        // Ignorar error si ya se cerró
      }
    }, duration);

    return notification;
  }

  /**
   * Enfoca la ventana/pestaña del navegador
   */
  focusWindow() {
    try {
      // Intentar enfocar la ventana
      if (window.focus) {
        window.focus();
      }
      
      // Si estamos en una pestaña, intentar hacerla visible
      if (document.hidden && document.visibilityState === 'hidden') {
        // El navegador debería automáticamente hacer visible la pestaña
        // cuando se hace clic en la notificación
      }
    } catch (error) {
      console.error('Error al enfocar ventana:', error);
    }
  }

  /**
   * Obtiene un ícono de teléfono como data URL
   * @returns {string} Data URL del ícono
   */
  getPhoneIcon() {
    // SVG básico de un teléfono convertido a data URL
    const svgIcon = `data:image/svg+xml;base64,${btoa(`
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
      </svg>
    `)}`;
    
    return svgIcon;
  }

  /**
   * Verifica si las notificaciones están habilitadas
   * @returns {boolean}
   */
  isEnabled() {
    return this.notificationPermission === 'granted';
  }

  /**
   * Obtiene el estado del permiso de notificaciones
   * @returns {string} 'granted', 'denied', 'default'
   */
  getPermissionStatus() {
    return Notification.permission;
  }
}

export default new NotificationService();